import { mapInvoiceToTableRow } from './invoiceUploadHelpers'

/**
 * Statuses that often go stale when an order is created after the invoice
 * (invoice-first flow). Re-run smart match before opening Match.
 */
export const AUTO_REMATCH_ON_MATCH_OPEN_STATUSES = new Set([
  'unprocessed',
  'no_match',
  'needs_action',
  'multiple_matches',
  'partial_match',
  'match_found',
])

/**
 * Narrower set for Match queue load/refresh (server rematches a capped batch).
 */
export const AUTO_REMATCH_ON_QUEUE_LOAD_STATUSES = new Set([
  'no_match',
  'needs_action',
])

function isTerminalInvoiceLifecycle(invoice = {}) {
  if (invoice?.isArchived) return true
  const lifecycle = invoice?.status || 'pending'
  return lifecycle === 'reconciled' || lifecycle === 'sentToXero'
}

/**
 * Soft-linked (attachedToPO) is not terminal for Match — rematch so Matching
 * leaves unprocessed / multiple_matches after a deliberate link.
 */
export function shouldAutoRematchBeforeMatchOpen(invoice = {}) {
  if (isTerminalInvoiceLifecycle(invoice)) return false
  const status = invoice?.matchingStatus || 'unprocessed'
  if (status === 'possible_duplicate') return false
  return AUTO_REMATCH_ON_MATCH_OPEN_STATUSES.has(status)
}

/**
 * Whether this row is eligible for the capped queue-load rematch pass.
 * Soft-linked rows are excluded here (handled on Match open / post-link).
 */
export function shouldAutoRematchOnQueueLoad(invoice = {}) {
  if (isTerminalInvoiceLifecycle(invoice)) return false
  const lifecycle = invoice?.status || 'pending'
  if (lifecycle === 'attachedToPO') return false
  const status = invoice?.matchingStatus || 'unprocessed'
  return AUTO_REMATCH_ON_QUEUE_LOAD_STATUSES.has(status)
}

/**
 * Merge POST /invoices/:id/rematch response into an invoices-table row.
 */
export function mergeRematchApiResultIntoInvoiceRow(
  invoiceRow = {},
  result = {},
) {
  const doc = result?.data
  if (doc && typeof doc === 'object') {
    const mapped = mapInvoiceToTableRow(doc)
    return {
      ...invoiceRow,
      ...mapped,
      id: invoiceRow.id || mapped.id,
      url: invoiceRow.url || mapped.url,
      tempUrl: invoiceRow.tempUrl || mapped.tempUrl,
    }
  }

  if (result?.matchingStatus) {
    return {
      ...invoiceRow,
      matchingStatus: result.matchingStatus,
    }
  }

  return invoiceRow
}

/**
 * Chunk 19 — soft copy when post-GR rematch-for-po hits errors or the cap.
 * GR save itself already succeeded; this must never look like a hard failure.
 */
export function buildPostGrRematchVisibilityNotice(result = {}, error = null) {
  if (error) {
    return {
      level: 'warning',
      message: 'Invoice matching may be out of date',
      description:
        'Goods receipting saved, but we couldn’t refresh invoice matching for this PO. Open Invoices and rematch if totals look stale.',
    }
  }

  const errors = Array.isArray(result?.errors) ? result.errors : []
  const truncated = Boolean(result?.truncated)
  if (!errors.length && !truncated) return null

  const parts = []
  if (errors.length) {
    parts.push(
      errors.length === 1
        ? '1 invoice couldn’t be re-checked'
        : `${errors.length} invoices couldn’t be re-checked`,
    )
  }
  if (truncated) {
    const checked = Number(result?.checked) || 0
    const total = Number(result?.totalCandidates) || 0
    parts.push(
      total > checked
        ? `re-checked ${checked} of ${total} open invoices (priority given to this PO and matching work)`
        : `stopped after ${checked} invoices to keep save fast`,
    )
  }

  return {
    level: 'warning',
    message: 'Invoice matching partially refreshed',
    description: `${parts.join(
      '; ',
    )}. Open Invoices if a match still looks wrong.`,
  }
}
